import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { Input } from "../components/ui/input";

export default function ProductsPage() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products/categories").then((r) => setCats(["All", ...r.data.categories]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (activeCat !== "All") params.category = activeCat;
    if (q) params.q = q;
    api.get("/products", { params }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [activeCat, q]);

  return (
    <div data-testid="products-page">
      {/* PAGE HEADER */}
      <section className="bg-carbon-950 text-white py-16 md:py-24 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/29506757/pexels-photo-29506757.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-carbon-950 via-carbon-950/80 to-carbon-950/40" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="overline text-amber-400 mb-3">Product Catalog</div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tight max-w-4xl leading-[1.05]">
            Heavy mining vehicle safety devices.
          </h1>
          <p className="mt-5 text-slate-300 max-w-2xl text-base md:text-lg leading-relaxed">
            DGMS-approved fire suppression, anti-collision, driver alert, and warning systems
            engineered for HEMM dumpers, excavators, and loaders.
          </p>
        </div>
      </section>

      {/* FILTERS */}
      <section className="border-b border-slate-200 sticky top-16 md:top-20 bg-white z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div className="flex items-center gap-2 overflow-x-auto" data-testid="category-filters">
            <Filter className="w-4 h-4 text-slate-500 shrink-0" />
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCat(c)}
                data-testid={`cat-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
                className={`text-xs font-semibold uppercase tracking-wider px-3 py-2 whitespace-nowrap border ${
                  activeCat === c
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="product-search-input"
              placeholder="Search products…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 rounded-none h-10"
            />
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-5 md:px-8 py-10 md:py-14">
        <div className="text-xs text-slate-500 font-mono mb-5">
          {loading ? "Loading…" : `${items.length} products`}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {items.map((p) => {
            const cover = (p.images && p.images[0]) || p.image_url;
            const coverUrl = cover && cover.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${cover}` : cover;
            return (
              <Link
                key={p.id}
                to={`/products/${p.id}`}
                data-testid={`product-card-${p.id}`}
                className="group card-premium block h-full overflow-hidden"
              >
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  {coverUrl ? (
                    <img src={coverUrl} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-slate-400 text-xs">No image</div>
                  )}
                </div>
                <div className="p-5">
                  <div className="text-[10px] tracking-[0.22em] text-amber-600 font-bold uppercase mb-2 font-mono">{p.category}</div>
                  <h3 className="h-card text-carbon mb-1 group-hover:text-amber-600 transition-colors">
                    {p.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 line-clamp-2 leading-relaxed">{p.short_description}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-700 group-hover:text-amber-600">
                    View details <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
        {!loading && items.length === 0 && (
          <div className="text-center py-20 text-slate-500">No products match your filters.</div>
        )}
      </section>
    </div>
  );
}
